import HighCharts from "highcharts";
import { getChartOptions } from "../HighCharts/chartGenerator";
import { getPieChartOptions } from "../HighCharts/pieChartGenerator";
import { getBubbleChartOptions } from "../HighCharts/bubbleChartGenerator";
import type { AxisChartSeries } from "../HighCharts/dataSeriesFormatters";
import type { ChatChartSpec, AxisChartSpec } from "./ChatChart.types";

const VALID_TYPES = new Set([
  "column",
  "bar",
  "line",
  "area",
  "pie",
  "donut",
  "bubble",
]);

const AXIS_TYPES = new Set(["column", "bar", "line", "area"]);

export function validateChartSpec(raw: unknown): ChatChartSpec {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("Chart spec must be a non-null object");
  }

  const obj = raw as Record<string, unknown>;

  if (typeof obj.type !== "string" || !VALID_TYPES.has(obj.type)) {
    throw new Error(
      `Invalid chart type "${String(obj.type)}". Supported: ${[...VALID_TYPES].join(", ")}`
    );
  }

  if (AXIS_TYPES.has(obj.type)) {
    if (!Array.isArray(obj.series) || obj.series.length === 0) {
      throw new Error(`Chart type "${obj.type}" requires a non-empty "series" array`);
    }
  } else if (obj.type === "pie" || obj.type === "donut") {
    if (!Array.isArray(obj.slices) || obj.slices.length === 0) {
      throw new Error(`Chart type "${obj.type}" requires a non-empty "slices" array`);
    }
  } else if (obj.type === "bubble") {
    if (!Array.isArray(obj.groups) || obj.groups.length === 0) {
      throw new Error(`Chart type "bubble" requires a non-empty "groups" array`);
    }
  }

  return raw as ChatChartSpec;
}

function buildAxisCategories(spec: AxisChartSpec): string[] {
  const labelSet = new Set<string>();
  for (const s of spec.series) {
    for (const p of s.points) {
      labelSet.add(p.label);
    }
  }
  return [...labelSet];
}

function buildAxisDataSeries(
  spec: AxisChartSpec,
  categories: string[]
): AxisChartSeries[] {
  const categoryIndex = new Map(categories.map((c, i) => [c, i]));
  return spec.series.map(s => ({
    name: s.name,
    data: categories.map(cat => ({
      x: categoryIndex.get(cat)!,
      y: s.points.find(p => p.label === cat)?.value ?? 0,
    })),
  }));
}

export function mapChartSpecToOptions(spec: ChatChartSpec): HighCharts.Options {
  switch (spec.type) {
    case "column":
    case "bar":
    case "line":
    case "area": {
      const categories = buildAxisCategories(spec);
      const dataSeries = buildAxisDataSeries(spec, categories);
      return getChartOptions({
        highChartType: spec.type,
        dataSeries,
        fontSize: 12,
        xAxisCategories: categories,
        allowExport: true,
        title: spec.title,
        subTitle: spec.subtitle,
        xAxisTitle: spec.xAxisTitle,
        yAxisTitle: spec.yAxisTitle,
        tooltipValueSuffix: spec.valueSuffix,
        tooltipValuePrefix: spec.valuePrefix,
      });
    }

    case "pie":
    case "donut": {
      const pieData = spec.slices.map(s => ({ name: s.name, y: s.value }));
      return getPieChartOptions({
        dataSeries: [
          {
            type: "pie",
            colorByPoint: true,
            data: pieData,
          },
        ],
        fontSize: 12,
        allowExport: true,
        title: spec.title,
        subTitle: spec.subtitle,
        formatSuffix: spec.valueSuffix,
        showAnimation: true,
        ...(spec.type === "donut" && { innerSize: "60%" }),
      });
    }

    case "bubble": {
      const bubbleSeries = spec.groups.map(g => ({
        name: g.name,
        data: g.items.map(i => ({ name: i.name, value: i.value })),
      }));
      return getBubbleChartOptions({
        highChartType: "packedbubble",
        dataSeries: bubbleSeries,
        fontSize: 12,
        allowExport: true,
        title: spec.title,
        subTitle: spec.subtitle,
        formatSuffix: spec.valueSuffix,
        formatPrefix: spec.valuePrefix,
      });
    }
  }
}
